import {
  ESessionStorageKeys,
  FinancingConfigDto,
  HomeInformationInput,
  Loader,
  Merchant,
  StepperFormFooter,
  useMerchantById,
  useMerchantFinancingConfig,
} from '@applyonline/shared'
import { scrollToTop } from '@applyonline/shared/utils'
import { EFinancingProgram, ELoanPurpose } from '@if/core/types'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppStore } from '../../data/AppContext'
import { isDefaultMerchant } from '../../data/merchant-selectors'
import { Constants } from '../../types'
import { Applicant } from '../../types/Schemas/ApplicantSchema'
import {
  LoanAmountInput,
  PersonalInformationInput,
  Prequalification,
  ServiceProviderInput,
} from '../../types/Schemas/PrequalificationSchema'
import AddressForm from './components/AddressForm'
import ContactInformationForm from './components/ContactInformationForm'
import EmploymentForm from './components/EmploymentForm'
import LoanAmountForm from './components/LoanAmountForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import ReviewPrequalification from './components/ReviewPrequalification'
import ServiceProviderForm from './components/ServiceProviderForm'
import StepProgressionBar from './components/StepProgressionBar'
import {
  buildDefaultPrequalification,
  getMonthlyIncome,
  useNewApplicationStepList,
  useSessionStorage,
} from './submit-app-hooks'

interface SubmitNewAppFormProps {
  initialMerchant: Merchant
  financingProgramConfig: FinancingConfigDto
}

export enum ApplicationStep {
  LoanAmount = 'loan-amount',

  ServiceProvider = 'service-provider',
  PersonalInformation = 'personal-information',
  Contact = 'contact',
  Employment = 'employment',
  Address = 'address',
  Review = 'review',
}

const SubmitNewAppForm = ({ initialMerchant, financingProgramConfig }: SubmitNewAppFormProps) => {
  const { merchantId: urlMerchantId, currentStep } = useParams()
  const appStore = useAppStore()
  const navigate = useNavigate()
  const [prequalInfo, setPrequalInfo] = useSessionStorage<Prequalification>(
    ESessionStorageKeys.PREQUAL_INFO,
    buildDefaultPrequalification(initialMerchant, financingProgramConfig),
  )

  useEffect(() => {
    const savedPrequalInfo = sessionStorage.getItem(ESessionStorageKeys.PREQUAL_INFO)
    if (!savedPrequalInfo) navigate(`/${urlMerchantId}/new-app/loan-amount`)
  }, [urlMerchantId, navigate])

  const [disableNext, setDisableNext] = useState(false)

  // on utilise le merchantId de l'url pour ne pas faire changer la liste de steps quand
  // quand l'utilisateur selectionne un marchand
  const steps = useNewApplicationStepList(urlMerchantId!)

  const cls = `form-section step-${currentStep}-active`

  useLayoutEffect(scrollToTop, [])

  const gotoStep = useCallback((stepPath: string) => {
    scrollToTop()
    navigate(`/${urlMerchantId}/new-app/${stepPath}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextStepAfter = useCallback(() => {
    const nextStepIdx = currentStep ? steps.indexOf(currentStep.toLowerCase()) + 1 : 1
    if (nextStepIdx < steps.length) {
      gotoStep(steps[nextStepIdx])
    }
  }, [currentStep, gotoStep, steps])

  const handlePrequalificationChanged = (
    data: LoanAmountInput | ServiceProviderInput | PersonalInformationInput,
  ) => {
    let updatedPrequalInfo
    if ((data as PersonalInformationInput).applicant) {
      const { applicant, ...otherData } = data as PersonalInformationInput
      updatedPrequalInfo = { ...prequalInfo, ...otherData, applicant: { ...prequalInfo.applicant, ...applicant } }
    } else {
      updatedPrequalInfo = {
        ...prequalInfo,
        ...(data as LoanAmountInput | ServiceProviderInput),
      }
    }
    setPrequalInfo(updatedPrequalInfo as Prequalification)

    if ((data as ServiceProviderInput).merchantId) appStore.setMerchantId((data as ServiceProviderInput).merchantId)
  }

  const handleApplicantChanged = (data: Partial<Applicant>) => {
    const update = {
      ...prequalInfo,
      applicant: { ...prequalInfo.applicant, ...data },
    }
    setPrequalInfo(update)
  }

  const handleAddressUpdated = (data: HomeInformationInput) => {
    handleApplicantChanged({
      currentAddress: data.currentAddress,
      spending: data.spending,
    } as Partial<Applicant>)
  }

  const monthlyIncome = useMemo(() => getMonthlyIncome(prequalInfo.applicant), [prequalInfo.applicant])

  return (
    <main className={cls}>
      <StepProgressionBar steps={steps} currentStep={currentStep!} />
      {currentStep === ApplicationStep.LoanAmount && (
        <LoanAmountForm
          prequalificationData={prequalInfo}
          onPrequalificationChanged={handlePrequalificationChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          financingProgramConfig={financingProgramConfig}
          showInvisalignQuestion={!isDefaultMerchant(urlMerchantId!)}
        />
      )}
      {currentStep === ApplicationStep.ServiceProvider && (
        <ServiceProviderForm
          prequalificationData={prequalInfo}
          loanPurposeId={prequalInfo.loanPurposeId}
          onPrequalificationChanged={handlePrequalificationChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
        />
      )}

      {currentStep === ApplicationStep.PersonalInformation && (
        <PersonalInformationForm
          prequalificationData={prequalInfo}
          onPrequalificationChanged={handlePrequalificationChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          loanPurposeId={prequalInfo.loanPurposeId}
        />
      )}

      {currentStep === ApplicationStep.Contact && (
        <ContactInformationForm
          onApplicantChanged={handleApplicantChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          applicantData={prequalInfo.applicant}
          loanPurposeId={prequalInfo.loanPurposeId}
          firstName={prequalInfo.applicant.firstName}
          middleName={prequalInfo.applicant.middleName}
          lastName={prequalInfo.applicant.lastName}
          languageId={prequalInfo.applicant.languageId}
        />
      )}
      {currentStep === ApplicationStep.Address && (
        <AddressForm
          applicantData={prequalInfo.applicant}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          onApplicantChanged={handleAddressUpdated}
          applicantName={prequalInfo.applicant.firstName}
          applicantBirthDate={prequalInfo.applicant.birthDate}
          monthlyIncome={monthlyIncome}
          setDisableNext={setDisableNext}
        />
      )}

      {currentStep === ApplicationStep.Employment && (
        <EmploymentForm
          applicantData={prequalInfo.applicant}
          onApplicantChanged={(data) => handleApplicantChanged(data)}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
        />
      )}

      {currentStep === ApplicationStep.Review && (
        <ReviewPrequalification
          gotoStep={gotoStep}
          prequalification={prequalInfo}
          financingProgramConfig={financingProgramConfig}
        />
      )}

      {currentStep !== ApplicationStep.Review && (
        <StepperFormFooter
          currentStepIndex={steps.indexOf(currentStep!)}
          steps={steps}
          disableBack={steps.indexOf(currentStep!) === 0}
          disableNext={disableNext}
          baseURL={`/${urlMerchantId}/new-app`}
        />
      )}
    </main>
  )
}

const SubmitNewAppPage = () => {
  const urlParams = useParams()
  const urlMerchantId = urlParams.merchantId!
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [merchant, isFetchingMerchant, error] = useMerchantById(urlMerchantId, EFinancingProgram.Personal)
  const [merchantFinancingConfig, isFetchingConfig] = useMerchantFinancingConfig(
    urlMerchantId,
    EFinancingProgram.Personal,
  )

  const financingConfig = useMemo<FinancingConfigDto | null>(() => {
    if (!merchant || !merchantFinancingConfig) return null

    const isMedicalCategory = [ELoanPurpose.Medical, ELoanPurpose.Aesthetics, ELoanPurpose.Dentistry].includes(
      merchant.serviceCategory,
    )
    const defaultMaxLoanAmount = isMedicalCategory ? Constants.MaxLoanAmountMedicard : Constants.MaxLoanAmount

    return {
      ...merchantFinancingConfig,
      maximumLoanAmount: [Constants.MaxLoanAmount, Constants.MaxLoanAmountMedicard].includes(
        merchantFinancingConfig.maximumLoanAmount,
      )
        ? defaultMaxLoanAmount
        : merchantFinancingConfig.maximumLoanAmount,
      minimumLoanAmount: merchantFinancingConfig.minimumLoanAmount ?? Constants.MinLoanAmount,
      maxLoanTerm: merchantFinancingConfig.maxLoanTerm ?? Constants.BaseMaxTerm,
    }
  }, [merchant, merchantFinancingConfig])

  useEffect(() => {
    if (error) {
      if ((error as AxiosError)?.response?.status === 403) {
        navigate('/get-merchant-error/inactive')
      } else if ((error as AxiosError)?.response?.status === 404) {
        navigate('/get-merchant-error/not-found')
      } else {
        navigate('/general-error-page/error')
      }
    }
  }, [error, navigate])

  if (isFetchingMerchant || isFetchingConfig) return <Loader title={t('common.withYouShortly')} description="" />
  if (error) return null
  if (merchant && financingConfig)
    return <SubmitNewAppForm initialMerchant={merchant} financingProgramConfig={financingConfig} />
  return null
}

export default SubmitNewAppPage
